import { createFileRoute } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import {
  coldStartProgressQuery,
  documentsQuery,
  draftColdStartLayer,
  engagementProfileQuery,
  pendingSetsQuery,
  planQuery,
  uploadDocument,
  type ColdStartLayer,
} from '../api/jedi'
import { useEngagement } from '../lib/engagement'
import type { ColdStartDraftResponse, ColdStartLayerProgress } from '../api/types'
import { PhasesToCheck } from '../shell/EngagementPhases'
import { MAX_UPLOAD_BYTES, MAX_UPLOAD_LABEL, oversizeWords } from '../shell/uploadLimits'

export const Route = createFileRoute('/_shell/cold-start')({
  component: ColdStart,
})

/**
 * Cold start: the SOW in hand, nothing pre-populated, to a working engagement.
 *
 * Built to `design/screens/jedi-app/Cold Start.dc.html`, which is a nine-stage
 * flow with a stage rail, per-stage gates, and a chat panel docked to the flow
 * rather than to the shell. That file RENDERS, which is why this is a
 * conformance job rather than a guess: the design was served and screenshotted
 * beside this while it was written.
 *
 * The four middle stages are this wave's work. The rest are shown because the
 * flow is nine stages and a rail that hides what is already done would misstate
 * where you are: uploading landed in wave 7, the engagement and its container
 * in wave 9, and intake review is the Home surface the routing law already puts
 * proposals on.
 *
 * ONLY THE PLAN WAITS, and the rail says so. It is a dependency and not a
 * preference: a deliverable requires its `enables` edge, so the plan cannot be
 * read into an engagement with no outcome, and one approved outcome is enough.
 * People and context are read in any order. The API refuses independently of
 * anything here (the gate lives at the seam), so the rail's locks are an
 * explanation rather than the enforcement.
 *
 * WHERE IT STANDS IS THE ENGAGEMENT'S, NOT THIS PAGE'S (Erik's demo-environment
 * rulings, 2026-10-22). Which layer was read, from which document, what it
 * proposed and what still waits are read from the API, so a person who leaves
 * the flow mid-way comes back to the contract they chose and the layers they
 * read. It used to live in page memory, lost on the first navigation, and a
 * layer read once looked unread. Reading a layer again replaces the set its
 * earlier read left pending, on the server, rather than adding a second.
 */

type Stage = {
  key: string
  name: string
  gate: string | null
  /** The layer this stage drafts, when it drafts one. */
  layer?: ColdStartLayer
  eyebrow: string
  question: string
  blurb: string
}

const STAGES: Stage[] = [
  {
    key: 'start',
    name: 'Start',
    gate: 'A contract is required',
    eyebrow: 'Start',
    question: 'Give J.E.D.I. everything you have.',
    blurb:
      'The contract is the one that matters. It sources the engagement record, the deliverables ' +
      'in the contract’s own words, the phases, and the customer’s vocabulary. Everything ' +
      'else sharpens the picture.',
  },
  {
    key: 'identity',
    name: 'Identity and container',
    gate: null,
    eyebrow: 'Identity and container',
    question: 'This engagement already exists.',
    blurb:
      'It was created deliberately, with its customer and the organisation above them, and its ' +
      'document library was provisioned at the same moment.',
  },
  {
    key: 'intake',
    name: 'Intake review',
    gate: null,
    eyebrow: 'Intake review',
    question: 'What your documents already proposed.',
    blurb:
      'Everything read out of your documents waits on Home, where proposals are decided. Nothing ' +
      'becomes real until you confirm it.',
  },
  {
    key: 'outcomes',
    name: 'Outcomes, the baseline',
    gate: 'Set the baseline to unlock the plan',
    layer: 'outcomes',
    eyebrow: 'Outcomes, the baseline',
    question: 'Set the baseline outcomes before anything else is framed.',
    blurb:
      'These become the baseline outcome: the agreed starting position. This is not the Outcome ' +
      'Gate, which comes weeks later after planning with the customer, and nothing confirmed here ' +
      'closes it. Approve one and the plan can be read.',
  },
  {
    key: 'people',
    name: 'People',
    gate: null,
    layer: 'people',
    eyebrow: 'People',
    question: 'Who is on this engagement, on both sides.',
    blurb:
      'Read from signature blocks, governance sections and the kickoff material. A role named ' +
      'without a person is an explicit ask for kickoff, not a silent gap, so J.E.D.I. leaves it ' +
      'for you rather than inventing somebody.',
  },
  {
    key: 'context',
    name: 'Context',
    gate: null,
    layer: 'context',
    eyebrow: 'Context',
    question: 'The rules this engagement lives by.',
    blurb:
      'Guardrails bound behaviour, constraints bound the plan, principles are the lean, ' +
      'definitions ground the vocabulary. Everything you confirm here is something J.E.D.I. never ' +
      'has to guess at again, and it seeds the Context surface.',
  },
  {
    key: 'plan',
    name: 'The plan',
    gate: null,
    layer: 'plan',
    eyebrow: 'The plan',
    question: 'Deliverables in the contract’s words, against the dates it fixes.',
    blurb:
      'Contractual deliverables are taken verbatim, each one enabling an outcome. Deeper ' +
      'decomposition is not asked here: task breakdown is progressive, per deliverable, when the ' +
      'team is about to work it.',
  },
  {
    key: 'provisioning',
    name: 'Provisioning',
    gate: 'System run',
    eyebrow: 'Provisioning',
    question: 'Nothing for you here.',
    blurb:
      'The container, the index and your membership were provisioned as one transaction when the ' +
      'engagement was created. If any part had failed, none of it would have landed.',
  },
  {
    key: 'kickoff',
    name: 'Kickoff readiness',
    gate: 'Exit gate',
    eyebrow: 'Kickoff readiness',
    question: 'What is set, and what rides along to kickoff.',
    blurb:
      'Open questions are carried forward rather than dropped. Anything still unanswered is ' +
      'yours to take into the room.',
  },
]

/**
 * Why a stage cannot run yet, in J.E.D.I.'s own words rather than as a
 * disabled control with no explanation. It mirrors the API's gate sentence
 * deliberately: the seam refuses independently of this surface, and a person
 * who reaches the refusal either way should read the same reason.
 */
function whyLocked(stage: Stage, documentId: string | null, documentRead: boolean): string {
  if (!documentId) {
    return 'Choose the contract on the first step and J.E.D.I. can read this one from it.'
  }
  if (!documentRead) {
    return 'The contract is still being read. This step opens as soon as it is through.'
  }
  if (stage.key === 'plan') {
    return (
      'The plan is read against the baseline outcomes, and this engagement has none set yet. ' +
      'Set the outcomes first and the plan can be read from the same contract.'
    )
  }
  return 'This step is not reachable yet.'
}

function ColdStart() {
  const { id: engagementId } = useEngagement()
  const [current, setCurrent] = useState(0)
  const [chosen, setChosen] = useState<string | null>(null)
  // A contract uploaded on the Start stage, chosen at once and read a moment later.
  const [uploaded, setUploaded] = useState<{ id: string; name: string } | null>(null)
  // What the last press on each stage answered: its gate sentence, whether it
  // replaced an earlier read, whether the document was whole. What a layer
  // HOLDS is the engagement's (`progress`), never this.
  const [answers, setAnswers] = useState<Record<string, ColdStartDraftResponse>>({})

  const profile = useQuery(engagementProfileQuery(engagementId))
  const documents = useQuery(documentsQuery(engagementId))
  const plan = useQuery(planQuery(engagementId))
  const pending = useQuery(pendingSetsQuery(engagementId))
  const progress = useQuery(coldStartProgressQuery(engagementId))
  const queryClient = useQueryClient()

  // The document the flow reads: the one chosen here, or else the one the
  // engagement read last, so coming back finds the contract already chosen.
  const documentId = chosen ?? progress.data?.documentId ?? null
  const readOf = (layer?: ColdStartLayer) => progress.data?.layers?.find((l) => l.layer === layer)

  const outcomes = plan.data?.filter((node) => node.nodeType === 'outcome') ?? []
  // STAGES is a non-empty literal and `current` is only ever moved by the two
  // clamped handlers below, so index 0 is the honest fallback rather than a
  // silent undefined the whole render would then have to guard.
  const stage = STAGES[current] ?? STAGES[0]!

  const draft = useMutation({
    mutationFn: (layer: ColdStartLayer) =>
      draftColdStartLayer(engagementId, documentId!, layer),
    onSuccess: (response) => {
      setAnswers((prior) => ({ ...prior, [response.layer]: response }))
      // The drafted set lands on the pending surface immediately, so the count
      // a person is about to act on cannot lag what was just written; and the
      // layer reads as read from the engagement's own answer.
      void queryClient.invalidateQueries({ queryKey: ['eng', engagementId, 'pending-sets'] })
      void queryClient.invalidateQueries({ queryKey: ['eng', engagementId, 'cold-start'] })
    },
  })

  // The order is enforced. A stage is reachable when everything it depends on
  // exists, which for the plan means an outcome exists to enable. The API
  // refuses independently; this only explains why.
  // `routed` is the terminal success state of ingest (uploaded → extracting →
  // routed | failed), for a document routed or only read alike. This read
  // `indexed` until the first run against a real engagement on dev, where it
  // silently offered NOTHING - and the harness had been passing, because the
  // stub I wrote invented an `indexed` state to match the filter I had
  // written. A stub is only as honest as the person writing it, and mine
  // agreed with my mistake.
  const readDocuments = documents.data?.filter((d) => d.state === 'routed') ?? []
  // A contract uploaded here is chosen at once, and is read a moment later.
  const documentRead = !!documentId && readDocuments.some((d) => d.id === documentId)

  const reachable = (index: number) => {
    const target = STAGES[index]
    if (!target) return false
    if (target.layer && !documentRead) return false
    if (target.key === 'plan') return outcomes.length > 0
    return true
  }

  // The contract uploaded on this stage, while it is still being read.
  const uploadedRow = uploaded ? documents.data?.find((d) => d.id === uploaded.id) : undefined
  const stillReading = uploaded && !readDocuments.some((d) => d.id === uploaded.id) ? uploaded.name : null

  return (
    <section className="cs">
      <header className="cs-head">
        <div className="cs-head-left">
          <span className="cs-mark">J.E.D.I.</span>
          <span className="cs-title">
            Cold start
            {profile.data?.name ? <span className="cs-eng"> · {profile.data.name}</span> : null}
          </span>
        </div>
        <div className="cs-head-right">
          <span className="cs-stageno">
            Stage {current + 1} of {STAGES.length}
          </span>
          <div className="cs-progress" aria-hidden="true">
            {/* No width transition: the bar reports which stage you are on, and
                a transitioned percentage reads as the previous stage's value
                whenever frames are throttled. The design says so in a comment
                and it is right. */}
            <div style={{ width: `${((current + 1) / STAGES.length) * 100}%` }} />
          </div>
        </div>
      </header>

      <div className="cs-body">
        <nav className="cs-rail" aria-label="Cold start stages">
          <ol>
            {STAGES.map((s, index) => {
              const done = s.layer ? Boolean(readOf(s.layer)?.setId) : index < current
              const locked = !reachable(index)
              return (
                <li key={s.key}>
                  <button
                    type="button"
                    // The lock LOCKS. It read as decoration in the first cut:
                    // the rail marked the plan stage locked and then navigated
                    // to it anyway, which is worse than no lock at all, because
                    // it states a rule and then breaks it in the same gesture.
                    disabled={locked}
                    className={
                      'cs-rail-item' +
                      (index === current ? ' is-current' : '') +
                      (done ? ' is-done' : '') +
                      (locked ? ' is-locked' : '')
                    }
                    onClick={() => setCurrent(index)}
                  >
                    <span className="cs-rail-no">{String(index + 1).padStart(2, '0')}</span>
                    <span className="cs-rail-name">{s.name}</span>
                    {locked && (
                      <span className="cs-rail-lock" title="Not yet reachable">
                        locked
                      </span>
                    )}
                  </button>
                </li>
              )
            })}
          </ol>
          <p className="cs-rail-foot">The plan can be read once an outcome is approved.</p>
        </nav>

        <div className="cs-main">
          <div className="cs-eyebrow">
            Stage {current + 1} of {STAGES.length} · {stage.eyebrow}
          </div>
          <h1 className="cs-q">{stage.question}</h1>
          {/* The blurb describes what this stage DOES. On a stage that cannot
              run yet it is a promise the surface cannot keep, so the reason
              takes its place. */}
          {reachable(current) && <p className="cs-blurb">{stage.blurb}</p>}

          {/* The phases are checked before the contract is read (ADR-0061 decision 7). */}
          {stage.key === 'start' && <PhasesToCheck engagementId={engagementId} />}

          {stage.key === 'start' && (
            <div className="cs-panel">
              <span className="cs-label">The contract J.E.D.I. reads</span>
              {documents.isPending && <p className="cs-note">Looking for your documents.</p>}
              {!documents.isPending && readDocuments.length === 0 && !stillReading && (
                <p className="cs-note">
                  Nothing here has finished being read yet. Upload the contract, and this step opens
                  as soon as it is through.
                </p>
              )}
              <ul className="cs-docs">
                {readDocuments.map((doc) => (
                  <li key={doc.id}>
                    <button
                      type="button"
                      className={'cs-doc' + (documentId === doc.id ? ' is-chosen' : '')}
                      onClick={() => setChosen(doc.id)}
                    >
                      <span className="cs-doc-name">{doc.fileName}</span>
                      <span className="cs-doc-state">{documentId === doc.id ? 'chosen' : 'choose'}</span>
                    </button>
                  </li>
                ))}
              </ul>
              <ContractUpload
                engagementId={engagementId}
                reading={stillReading}
                failed={uploadedRow?.state === 'failed'
                  ? `${uploadedRow.fileName} stopped at ${uploadedRow.failureStage}. ${uploadedRow.failureReason ?? ''}`.trim()
                  : null}
                onUploaded={(id, name) => {
                  setUploaded({ id, name })
                  setChosen(id)
                }}
              />
            </div>
          )}

          {stage.key === 'identity' && (
            <div className="cs-panel">
              <span className="cs-label">The engagement</span>
              <p className="cs-fact">{profile.data?.name ?? engagementId}</p>
              <p className="cs-note">
                The customer and the organisation above them are two tiers, and that is what decides
                who sees what for the life of the engagement.
              </p>
            </div>
          )}

          {stage.key === 'intake' && (
            <div className="cs-panel">
              <span className="cs-label">Waiting on Home</span>
              <p className="cs-fact">
                {pending.data?.length ?? 0} proposal{(pending.data?.length ?? 0) === 1 ? '' : 's'}
              </p>
              <p className="cs-note">
                Proposals are decided on Home, which is the one place engagement level proposals are
                acted on. This step is a pointer, not a second decision surface.
              </p>
            </div>
          )}

          {stage.layer && !reachable(current) && (
            <div className="cs-panel">
              <p className="cs-gate-line">{whyLocked(stage, documentId, documentRead)}</p>
            </div>
          )}

          {stage.layer && reachable(current) && (
            <LayerStage
              stage={stage}
              documentId={documentId}
              read={readOf(stage.layer)}
              answer={answers[stage.layer]}
              pendingDraft={draft.isPending}
              error={draft.error as Error | null}
              onDraft={() => draft.mutate(stage.layer!)}
            />
          )}

          {stage.key === 'provisioning' && (
            <div className="cs-panel">
              <span className="cs-label">Done at creation</span>
              <p className="cs-note">
                The container, the search index and your membership all landed together. There is
                nothing to run again.
              </p>
            </div>
          )}

          {stage.key === 'kickoff' && (
            <div className="cs-panel">
              <span className="cs-label">Where this engagement stands</span>
              <ul className="cs-summary">
                <li>
                  <span>Baseline outcomes</span>
                  <b>{outcomes.length} set</b>
                </li>
                {(['outcomes', 'people', 'context', 'plan'] as ColdStartLayer[]).map((layer) => (
                  <li key={layer}>
                    <span>{layer} proposed</span>
                    <b>{readOf(layer)?.partCount ?? 0}</b>
                  </li>
                ))}
                <li>
                  <span>Waiting on your decision</span>
                  <b>{pending.data?.length ?? 0}</b>
                </li>
              </ul>
              <p className="cs-note">
                Nothing here is committed. Everything proposed waits on Home until you accept or
                dismiss it.
              </p>
            </div>
          )}

          <footer className="cs-foot">
            <button
              type="button"
              className="cs-back"
              disabled={current === 0}
              onClick={() => setCurrent((n) => Math.max(0, n - 1))}
            >
              Back
            </button>
            <button
              type="button"
              className="cs-next"
              disabled={current === STAGES.length - 1 || !reachable(current + 1)}
              onClick={() => setCurrent((n) => Math.min(STAGES.length - 1, n + 1))}
            >
              Continue
            </button>
            {stage.gate && <span className="cs-gate">{stage.gate}</span>}
          </footer>
        </div>
      </div>
    </section>
  )
}

/**
 * A drafting stage. One button, one call, one set: the reviewable unit is the
 * SET, which is what "one card, not thirty" means concretely, so this reports
 * what the set contains rather than listing its parts as separate cards.
 *
 * A refusal arrives as a `gate` sentence on a 200, not as an error status. That
 * is deliberate at the API and it matters here: the refusal is content the
 * person on this stage reads, and rendering it as an error would bury the one
 * sentence that says what to do next.
 */
function LayerStage({
  stage,
  documentId,
  read,
  answer,
  pendingDraft,
  error,
  onDraft,
}: {
  stage: Stage
  documentId: string | null
  /** The layer's last read, as the engagement holds it. */
  read: ColdStartLayerProgress | undefined
  /** What the last press here answered, this visit. */
  answer: ColdStartDraftResponse | undefined
  pendingDraft: boolean
  error: Error | null
  onDraft: () => void
}) {
  if (!documentId) {
    return (
      <div className="cs-panel">
        <p className="cs-note">
          Choose the contract on the first step and J.E.D.I. can read this one from it.
        </p>
      </div>
    )
  }

  const wasRead = !!read?.setId
  // Read again only from the same document: another document is another read,
  // and its set waits beside the first rather than replacing it.
  const again = wasRead && read?.documentId === documentId

  return (
    <div className="cs-panel">
      <span className="cs-label">Read from the contract</span>

      {!wasRead && !pendingDraft && (
        <p className="cs-note">
          Nothing read yet for this step. J.E.D.I. reads the whole contract, not a search of it.
        </p>
      )}

      {pendingDraft && <p className="cs-note">Reading the contract.</p>}

      {answer?.gate && <p className="cs-gate-line">{answer.gate}</p>}

      {wasRead && (
        <>
          <p className="cs-fact">
            {read!.partCount} proposed{read!.fileName ? ` from ${read!.fileName}` : ''}, as one set to review
          </p>
          <p className="cs-note">
            {Number(read!.pending) > 0
              ? 'It is waiting on Home. Decide all of it, or tick the records you are sure of and decide those.'
              : 'Decided on Home.'}
          </p>
        </>
      )}

      {answer && Number(answer.superseded) > 0 && (
        <p className="cs-note">This read replaces the one before it.</p>
      )}

      {answer && !answer.wholeDocument && (
        <p className="cs-note">
          That document is longer than one read carries, so this covers the front of it rather than
          all of it.
        </p>
      )}

      {error && <p className="cs-gate-line">{error.message}</p>}

      <button type="button" className="cs-read" onClick={onDraft} disabled={pendingDraft}>
        {again ? 'Read it again' : `Read the ${stage.layer}`}
      </button>
    </div>
  )
}

/**
 * Cold start's own upload (Erik's demo-environment rulings, 2026-10-22). The
 * contract uploaded here is READ, never routed: kept, searchable and citable,
 * and nothing is proposed from it statement by statement. Routed as well, its
 * risks, people and rules would arrive on Home one statement at a time, and
 * the layers below would then propose the same things again from the same
 * contract. Inputs keeps the choice for every other document.
 */
function ContractUpload({
  engagementId,
  reading,
  failed,
  onUploaded,
}: {
  engagementId: string
  /** The contract uploaded here, while it is still being read. */
  reading: string | null
  /** Why reading it stopped, in the ingest's own words. */
  failed: string | null
  onUploaded: (documentId: string, fileName: string) => void
}) {
  const queryClient = useQueryClient()
  const input = useRef<HTMLInputElement>(null)
  const [oversize, setOversize] = useState<string | null>(null)
  const upload = useMutation({
    mutationFn: (file: File) => uploadDocument(engagementId, file, false),
    onSuccess: (result, file) => {
      onUploaded(result.documentId, file.name)
      // The row should appear on the upload, not on the next tick.
      void queryClient.invalidateQueries({ queryKey: ['eng', engagementId, 'documents'] })
    },
  })

  return (
    <div className="cs-upload">
      <input
        ref={input}
        type="file"
        accept=".txt,.docx,.pdf"
        className="upload-input"
        onChange={(event) => {
          const file = event.target.files?.[0]
          event.target.value = ''
          if (!file) return
          if (file.size > MAX_UPLOAD_BYTES) {
            setOversize(oversizeWords(file))
            return
          }
          setOversize(null)
          upload.mutate(file)
        }}
      />
      <button type="button" className="cs-read" disabled={upload.isPending} onClick={() => input.current?.click()}>
        {upload.isPending ? 'Uploading…' : 'Upload the contract'}
      </button>
      {reading && !failed && <p className="cs-note">Reading {reading}.</p>}
      {failed && <p className="cs-gate-line">{failed} Upload it again to retry.</p>}
      {oversize && <p className="cs-gate-line">{oversize}</p>}
      {upload.error && (
        <p className="cs-gate-line">
          {upload.error.message.includes('API 413')
            ? `That file is over the ${MAX_UPLOAD_LABEL} limit per file. Nothing was stored.`
            : upload.error.message}
        </p>
      )}
    </div>
  )
}
